import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import cloneDeep from 'lodash-es/cloneDeep';
import { map, Observable, switchMap } from 'rxjs';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services/caching.service';
import { UserApiService } from 'shared/services/userApi.service';

@Injectable({
    providedIn: 'root',
})
export class InvitedUsersService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private translate: TranslateService,
        private userApiService: UserApiService,
    ) {
        this.apollo = this.apolloProvider.use('usersAPI');
    }

    NAME = 'InvitedUsersGridService';

    getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.userApiService.getLoginInformation().pipe(
            switchMap((userData: CurrentSupplierUserInterface | CurrentShijiUserInterface) => {
                const supplierId = (userData as CurrentSupplierUserInterface)?.supplierId;

                return this.apollo
                    .query({
                        query: gql`
                        query getInvitedUsers (
                            $companyName: String,
                            $email: String,
                            $validUntil: DateOnly,
                            $isActive: Boolean,
                            $orderBy: String,
                            $orderDirection: OrderDirection) {
                            invitedUsers {
                                search(searchSettings: {
                                    pageNumber: ${pageNumber},
                                    pageSize:${pageSize},
                                    orderBy: $orderBy,
                                    orderDirection: $orderDirection
                                },
                                    filtering: {
                                        companyName: $companyName,
                                        email: $email,
                                        validUntil: $validUntil,
                                        isActive: $isActive
                                    })  {
                                    data {
                                        id,
                                        supplierId,
                                        ${!supplierId ? ', companyName,' : ''}
                                        email,
                                        validUntil,
                                        isShijiInvite,
                                        inviteLink
                                    },
                                    totalCount
                                }
                            }}`,
                        fetchPolicy: 'no-cache',
                        variables: {
                            orderBy:
                                params.request.sortModel[0]?.colId === 'inviteStatus'
                                    ? 'validUntil'
                                    : (params.request.sortModel[0]?.colId ?? 'companyName'),
                            //In case of filtering by "Invite Status" we'll actually filter by "validUntil", so we need to negate the orderDirection
                            orderDirection:
                                params.request.sortModel[0]?.colId === 'inviteStatus'
                                    ? params.request.sortModel[0]?.sort?.toUpperCase() === 'ASC'
                                        ? 'DESC'
                                        : 'ASC'
                                    : (params.request.sortModel[0]?.sort?.toUpperCase() ?? 'ASC'),
                            companyName: (params.request.filterModel as FilterModel)['companyName']?.filter,
                            validUntil: (params.request.filterModel as FilterModel)['validUntil']?.filter
                                ? formatDate((params.request.filterModel as FilterModel)['validUntil']?.filter, 'yyyy-MM-dd', 'en-US')
                                : undefined,
                            email: (params.request.filterModel as FilterModel)['email']?.filter,
                            isActive: (params.request.filterModel as FilterModel)['inviteStatus']?.filter
                                ? (params.request.filterModel as FilterModel)['inviteStatus']?.filter === 'users.invitedUsers.active'
                                : undefined,
                        },
                    })
                    .pipe(
                        map((response: any) => {
                            let clonedResponse = cloneDeep(response.data.invitedUsers.search.data);
                            clonedResponse = clonedResponse.map((row: any) => {
                                let computedDate = new Date(row.validUntil).getTime();
                                const millisecondsInCurrentDay = computedDate % 86400000;
                                computedDate = computedDate - millisecondsInCurrentDay + 86399999;

                                if (computedDate < Date.now()) {
                                    row.inviteStatus = 'users.invitedUsers.expired';
                                } else {
                                    row.inviteStatus = 'users.invitedUsers.active';
                                }

                                return row;
                            });

                            return {
                                ...response.data.invitedUsers.search,
                                data: clonedResponse,
                            };
                        }),
                    );
            }),
        );
    }

    reactivateExpiredInvitation(invitationId: string): Observable<any> {
        const reactivateExpiredInvitation = `
            mutation reactivateExpiredInvitation {
                invitedUsers {
                    reactivateInvitedUser (invitedUserId: "${invitationId}") {
                        data,
                        statusCode,
                        message
                    }
                }
            }`;

        return this.apollo
            .mutate({
                mutation: gql(reactivateExpiredInvitation),
            })
            .pipe(
                map((response: any) => {
                    return response.data.invitedUsers.reactivateInvitedUser.data;
                }),
            );
    }

    deleteUserInvitation(invitationId: string): Observable<any> {
        const deleteUserInvitation = `
            mutation deleteUserInvitation {
                invitedUsers {
                    deleteUserInvitation (userInviteId: "${invitationId}") {
                        data,
                        statusCode,
                        message
                    }
                }
            }`;

        return this.apollo
            .mutate({
                mutation: gql(deleteUserInvitation),
            })
            .pipe(
                map((response: any) => {
                    return response.data.invitedUsers.deleteUserInvitation.data;
                }),
            );
    }
}
